import React, { useState } from 'react';
import { JSONTree } from 'react-json-tree';
import { useFormik } from 'formik';
import { type AlertColor, Card, RadioGroup } from '@mui/material';
import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { CoronavirusOutlined, FlagCircleOutlined } from '@mui/icons-material';

import { useGetRefereeReportsByApplicationNo } from '../graphql/useGetRefereeReport';

import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Accordion,
  Alert,
  type AlertDetails,
  Button,
  Checkbox,
  DatePicker,
  FILE_MANAGE_ORDER,
  type FileData,
  FilesPanel,
  Loading,
  Radio,
  SearchField,
  Select,
  TextField,
} from '../../../../components';
import {
  AssessmentGrid as RowDisplay,
  type RowData,
} from '../../../Assessment';
import { FileBox } from '../../../../components/FilesPanel/FileBox';
import { useAuth } from '../../../../contexts';

dayjs.extend(utc);

type HandleApplicationSearchProps = {
  applicationNo: string;
  setApplicationNo: (data: string) => void;
  handleSearch: () => void;
  placeholder?: string;
};

const HandleApplicationSearch: React.FC<HandleApplicationSearchProps> = ({
  applicationNo,
  setApplicationNo,
  handleSearch,
  placeholder,
}: HandleApplicationSearchProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setApplicationNo(event.target.value);
  };

  return (
    <SearchField
      content={applicationNo}
      placeholder={placeholder ?? 'Application No'}
      handleContentChange={handleChange}
      handleSearch={handleSearch}
    />
  );
};

export const CompensationFileUpload: React.FunctionComponent = () => {
  const [files, setFiles] = useState<FileData[]>([]);

  const handleUploadFile = (order: number, id: string): boolean => {
    const newPersonalStatement: FileData[] = [...files];
    switch (order) {
      case FILE_MANAGE_ORDER.UPLOAD: {
        setFiles([
          ...newPersonalStatement,
          {
            id,
            name: '',
            link: '',
          },
        ]);
        break;
      }
      case FILE_MANAGE_ORDER.REMOVE:
        setFiles(newPersonalStatement.filter(doc => doc.id !== id));
        break;
      default:
    }
    return true;
  };

  return (
    <div className="flex flex-row ">
      {/* Upload File Panel */}

      <div>
        <FilesPanel
          caption={'Compensation Files'}
          files={files}
          handleFilesChange={handleUploadFile}
        />
      </div>
      {/* TODO: add a upload history in the next version */}
      <div>
        <div className="ml-2 mr-2">
          <p>
            <strong>Files: (Please copy the file information)</strong>
          </p>
        </div>
        <div className="w-[800px]">
          <JSONTree data={JSON.stringify(files)} hideRoot={true} />
        </div>
      </div>
    </div>
  );
};

const RegenerateRefereeFile: React.FC<{
  formId: string;
}> = ({ formId }) => {
  const [adminRegenerateRefereeReports] = useMutation(gql`
    mutation ($formId: String!) {
      adminRegenerateRefereeReports(formId: $formId)
    }
  `);

  const handleRegenerate = (formId: string): void => {
    void adminRegenerateRefereeReports({
      variables: { formId },
    });
  };

  return (
    <div className="flex flex-row items-center justify-between">
      <Button
        variant="outlined"
        onClick={() => {
          handleRegenerate(formId);
        }}
      >
        Confirm
      </Button>
    </div>
  );
};

const CompensateRefereeFile: React.FC<{
  refereeId: string;
}> = ({ refereeId }) => {
  const [fileName, setFileName] = useState('');
  const [fileKey, setFileKey] = useState('');

  const [adminUploadRefereeReports] = useMutation(gql`
    mutation ($refereeId: String!, $fileName: String!, $fileKey: String!) {
      adminUploadRefereeReports(
        refereeId: $refereeId
        file: { id: $fileKey, name: $fileName, link: $fileKey }
      )
    }
  `);

  const handleCompensate = (refereeId: string): void => {
    void adminUploadRefereeReports({
      variables: { refereeId, fileName, fileKey },
    });
  };

  return (
    <div className="flex flex-row items-center justify-between">
      <TextField
        label="File Name"
        value={fileName}
        onChange={e => {
          setFileName(e.target.value);
        }}
      />
      <TextField
        label="File Key"
        value={fileKey}
        onChange={e => {
          setFileKey(e.target.value);
        }}
      />
      <Button
        variant="outlined"
        onClick={() => {
          handleCompensate(refereeId);
        }}
      >
        Confirm
      </Button>
    </div>
  );
};

type RefereeReportData = {
  applicationNo: string;
  refereeName: string;
  refereeStatus: string;
  refereeSubmissionDate: string;
  refereeFile: React.ReactNode;
  regenerateRefereeFile: React.ReactNode;
  compensateRefereeFile: React.ReactNode;
};

export const HandleRefereeReport = (): React.JSX.Element => {
  const [applicationNo, setApplicationNo] = useState<string>('');
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [refereeReportData, setRefereeReportData] = useState<
    RefereeReportData[]
  >([]);
  const { handleLoading } = useAuth();

  const handleDisplayAlert = (
    alertType: AlertColor,
    alertContent: string,
  ): void => {
    setOpenAlert(true);
    setAlertDetails({
      alertType,
      alertContent,
    });
  };

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleUpdateRefereeReportData = async (): Promise<void> => {
    try {
      const data = await useGetRefereeReportsByApplicationNo(
        client,
        applicationNo,
      );
      if ((data ?? [])?.length === 0) {
        setAlertDetails({
          alertContent: `NOT FOUND`,
          alertType: 'error',
        });
        setOpenAlert(true);
        setRefereeReportData([]);

        return;
      }

      const information: RefereeReportData[] = data?.map((entry: any) => {
        const {
          applicationNo,
          refereeName,
          refereeId,
          formId,
          status,
          repliedAt,
          file,
        } = entry;

        return {
          applicationNo,
          refereeName,
          refereeStatus: status,
          refereeSubmissionDate: repliedAt,
          refereeFile: file !== null && (
            <FileBox
              file={file}
              fileExtension={'pdf'}
              handleDisplayAlert={handleDisplayAlert}
              index={0}
              removeFile={() => {}}
              handleLoading={handleLoading}
            />
          ),
          regenerateRefereeFile: <RegenerateRefereeFile formId={formId} />,
          compensateRefereeFile: (
            <CompensateRefereeFile refereeId={refereeId} />
          ),
        };
      });

      setRefereeReportData(information);
      setAlertDetails({
        alertContent: `Found`,
        alertType: 'success',
      });
      setOpenAlert(true);
    } catch (error) {
      setAlertDetails({
        alertContent: `Error Occurs: ${(error as Error)?.message}`,
        alertType: 'error',
      });
      setOpenAlert(true);
    }
  };

  const rows = refereeReportData?.map(
    (record: any) =>
      Object.entries(record).map(([key, value]) => ({
        label: key,
        value,
      })) as RowData[],
  );

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <div>
        <HandleApplicationSearch
          applicationNo={applicationNo}
          setApplicationNo={setApplicationNo}
          handleSearch={handleUpdateRefereeReportData}
        />
      </div>
      <div>
        {rows?.map((row: RowData[], index: number) => (
          <Card
            key={index}
            sx={{
              boxShadow: 'lg',
              backgroundColor: '#f5f5f5',
              padding: '2rem 4rem',
              width: '100%',
            }}
          >
            <RowDisplay rows={row} />
          </Card>
        ))}
      </div>
    </div>
  );
};

export const HandleApplicationFee = (): React.JSX.Element => {
  const [applicationNo, setApplicationNo] = useState<string>('');
  const [applicationDeadline, setApplicationDeadline] = useState<any>(null);

  const handleApplicationNoDisplay = (): string => {
    if (applicationNo.length === 9) {
      return applicationNo;
    }
    return '';
  };

  const [adminChangeApplicationFeeDeadline] = useMutation(gql`
    mutation ($applicationNo: String!, $deadline: DateTime!) {
      adminChangeApplicationFeeDeadline(
        applicationNo: $applicationNo
        deadline: $deadline
      )
    }
  `);

  const handleChangeApplicationFeeDeadline = (): void => {
    if (applicationNo?.length !== 9) {
      return;
    }
    void adminChangeApplicationFeeDeadline({
      variables: { applicationNo, deadline: applicationDeadline },
    });
  };

  const handleDateChange = (event: any): void => {
    if (event.$isDayjsObject === true) {
      setApplicationDeadline(event);
    } else if (event?.target !== null) {
      setApplicationDeadline(dayjs(event.value ?? new Date()));
    }
  };

  const information = {
    applicationNo,
    // applicationId : '',
    // paymentType : '',
    // paymentStatus : '',
    // paymentDeadline : '',
    // paymentDate: '',
    newPaymentDeadline: (
      <div className="flex flex-row items-center justify-between">
        <DatePicker
          value={dayjs(applicationDeadline)}
          onChange={handleDateChange}
        />
        <Button variant="outlined" onClick={handleChangeApplicationFeeDeadline}>
          Confirm
        </Button>
      </div>
    ),
  };

  const rows = Object.entries(information).map(([key, value]) => ({
    label: key,
    value,
  }));

  return (
    <div>
      <div>
        <HandleApplicationSearch
          applicationNo={applicationNo}
          setApplicationNo={setApplicationNo}
          handleSearch={handleApplicationNoDisplay}
        />
      </div>
      <div>
        <Card
          sx={{
            boxShadow: 'lg',
            backgroundColor: '#f5f5f5',
            padding: '2rem 4rem',
            width: '100%',
          }}
        >
          <RowDisplay rows={rows} />
        </Card>
      </div>
    </div>
  );
};

type AdminGPARule = {
  ruleName: string;
  useOverallGPA?: boolean;
  type: 'REGIONAL' | 'INSTITUTIONAL';
  increasingGPAScale?: boolean;
  mapping: Record<string, number>;
  maxGPARange?: number;
  minGPARange?: number;
  defaultRule?: boolean;
  note?: string;
  content?: string;
  dryRun?: number;
};

export const HandleGPARule: React.FC = () => {
  const formik = useFormik<AdminGPARule>({
    initialValues: {
      ruleName: '',
      useOverallGPA: true,
      type: 'INSTITUTIONAL',
      increasingGPAScale: true,
      mapping: {},
      maxGPARange: 0,
      minGPARange: 0,
      defaultRule: false,
      note: '',
      content: '',
      dryRun: 0,
    },
    onSubmit: async () => {
      await handleSubmit();
    },
  });

  const [dryRunResult, setDryRunResult] = useState<any>('');
  const [adminDryRun] = useMutation(
    gql`
      mutation (
        $rule: InterpolatingGPARulePayloadInput!
        $input: ConvertGPAInputV2!
      ) {
        adminDryRun(rule: $rule, input: $input)
      }
    `,
    {
      variables: {
        rule: {
          ruleName: formik.values.ruleName,
          // useOverallGPA: formik.values.useOverallGPA,
          type: formik.values.type,
          // increasingGPAScale: formik.values.increasingGPAScale,
          mapping: JSON.stringify(formik.values.mapping),
          maxGPARange: Number(formik.values.maxGPARange),
          minGPARange: Number(formik.values.minGPARange),
          // defaultRule: formik.values.defaultRule,
          note: formik.values.note,
          content: formik.values.content,
        },
        input: {
          ruleId: '',
          originalGPA: Number(formik.values.dryRun),
          originalGPAOutOf: Number(formik.values.maxGPARange),
        },
      },
      onCompleted: (data: any) => {
        setDryRunResult(data);
      },
    },
  );

  const [adminAddRule] = useMutation(
    gql`
      mutation ($rule: InterpolatingGPARulePayloadInput!) {
        adminAddRule(rule: $rule)
      }
    `,
    {
      variables: {
        rule: {
          ruleName: formik.values.ruleName,
          // useOverallGPA: formik.values.useOverallGPA,
          type: formik.values.type,
          // increasingGPAScale: formik.values.increasingGPAScale,
          mapping: JSON.stringify(formik.values.mapping),
          maxGPARange: Number(formik.values.maxGPARange),
          minGPARange: Number(formik.values.minGPARange),
          // defaultRule: formik.values.defaultRule,
          note: formik.values.note,
          content: formik.values.content,
        },
      },
    },
  );

  const handleDryRun = async (): Promise<void> => {
    await adminDryRun();
  };

  const handleSubmit = async (): Promise<void> => {
    await adminAddRule();
  };

  const [mapDisplay, setMapDisplay] = useState<any>('');
  const [error, setError] = useState<string>('');

  const handleDisplay = (map: Record<number | string, number>): string => {
    return JSON.stringify(
      Object.entries(map ?? {})
        .map(([key, value]) => `${key}:${value}`)
        .reduce((acc, curr) => `${acc}\n${curr}`, ''),
    );
  };

  const handleMapChange = async (e: any): Promise<void> => {
    const newMap: Record<number | string, number> = {};
    try {
      const mapping = e.target.value;
      setMapDisplay(mapping);
      const parseMap = mapping
        ?.split('\n')
        ?.map((entry: any) => entry.split(':'));
      parseMap.forEach(([key, value]: any) => {
        if (key === '' && value === '') {
          return;
        }
        if (key === '' || value === '') {
          throw new Error('Key or value must be complete');
        }
        if (isNaN(parseInt(key))) {
          throw new Error('Key must be a number');
        }
        if (isNaN(parseInt(value))) {
          throw new Error('Value must be a number');
        }

        newMap[key.toString().trim()] = Number(value?.trim());
      });
      setError('');
    } catch (e: any) {
      formik.setFieldError('mapping', e.message);
      setError(e.message);
      return;
    }
    await formik.setFieldValue('mapping', newMap);
  };

  const handleMinGPARange = (value: any): React.ReactNode => {
    const contain = formik.values.mapping[`${value}`];
    if (contain === null || contain === undefined) {
      return <div>Warning: {value} is not presented in the mapping.</div>;
    }
    return <div />;
  };

  return (
    <div className="flex flex-col">
      <Accordion
        details={
          <div>
            <div className="m-4">
              <RadioGroup
                value={formik.values.useOverallGPA}
                onChange={async e =>
                  await formik.setFieldValue(
                    'useOverallGPA',
                    e.target.value === 'true',
                  )
                }
                name="useOverallGPA"
              >
                <Radio
                  label="Use Overall GPA"
                  value={true}
                  disabled={true}
                  checked={formik.values.useOverallGPA === true}
                />
                <Radio
                  label="No"
                  value={false}
                  disabled={true}
                  checked={formik.values.useOverallGPA === false}
                />
              </RadioGroup>
            </div>
            <div className="m-4">
              <RadioGroup
                value={formik.values.increasingGPAScale}
                onChange={async e =>
                  await formik.setFieldValue(
                    'increasingGPAScale',
                    e.target.value === 'true',
                  )
                }
                name="useOverallGPA"
              >
                <Radio
                  label="Increasing GPA Scale"
                  value={true}
                  checked={formik.values.increasingGPAScale === true}
                />
                <Radio
                  label="Decreasing GPA Scale"
                  value={false}
                  checked={formik.values.increasingGPAScale === false}
                />
              </RadioGroup>
            </div>
            <div className="m-4">
              <Checkbox
                label="Default Rule"
                disabled={true}
                value={formik.values.defaultRule ?? false}
                onChange={formik.handleChange}
                name="defaultRule"
              />
            </div>
          </div>
        }
        title="Advanced Settings (Beta)"
      />

      <div className="m-4">
        <Select
          options={[
            {
              value: 'REGIONAL',
              label: 'Regional',
            },
            {
              value: 'INSTITUTIONAL',
              label: 'Institutional (Full Name)',
            },
          ]}
          label="Type"
          value={formik.values.type}
          onChange={formik.handleChange}
          name="type"
          fullWidth
        />
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="m-4">
          <TextField
            label="Rule Name"
            value={formik.values.ruleName}
            onChange={formik.handleChange}
            name="ruleName"
            fullWidth
          />
          <div>
            For institution, please exact match the institution name; for
            region, please add DEFAULT - before the official name.
          </div>
        </div>
        <div className="m-4">
          <TextField
            label="Mapping"
            value={mapDisplay ?? handleDisplay(formik.values.mapping)}
            onChange={handleMapChange}
            error={
              formik.errors.mapping !== null &&
              formik.errors.mapping !== undefined
            }
            name="mapping"
            multiline
            minRows={3}
            fullWidth
          />
          {error === '' || error === null || error === undefined ? (
            <FlagCircleOutlined />
          ) : (
            <div>
              <CoronavirusOutlined />
              {error}
            </div>
          )}
          <div>
            Please use Original Score: Target Score with new line as a new
            entry. For example, a 70-100 score to 2.0-4.0 scale, use `70:2.0
            80:3.0 90:3.5 100:4.0`.
          </div>
        </div>
        <div className="m-4">
          <TextField
            label="Max GPA Range"
            value={formik.values.maxGPARange}
            onChange={formik.handleChange}
            name="maxGPARange"
            fullWidth
          />
          {handleMinGPARange(formik.values.maxGPARange)}
        </div>
        <div className="m-4">
          <TextField
            label="Min GPA Range"
            value={formik.values.minGPARange}
            onChange={formik.handleChange}
            name="minGPARange"
            fullWidth
          />
          {handleMinGPARange(formik.values.minGPARange)}
        </div>

        <div className="m-4">
          <TextField
            label="Note"
            value={formik.values.note}
            onChange={formik.handleChange}
            multiline
            name="note"
            fullWidth
            minRows={3}
          />
        </div>
        <div className="m-4">
          <TextField
            label="Content"
            value={formik.values.content}
            onChange={formik.handleChange}
            multiline
            name="content"
            fullWidth
            minRows={3}
          />
        </div>
        <div className="m-4 p-4 bg-gray-100">
          <TextField
            label="Dry Run: GPA"
            value={formik.values.dryRun}
            onChange={formik.handleChange}
            name="dryRun"
            fullWidth
          />
          <Button variant="outlined" onClick={handleDryRun}>
            Dry Run
          </Button>
          {Boolean(dryRunResult) && <div>{JSON.stringify(dryRunResult)}</div>}
        </div>
        <div className="m-4 flex flex-row">
          <Button type="submit" variant="outlined">
            Submit
          </Button>
        </div>
      </form>
    </div>
  );
};

export const HandleOfferManagement: React.FC = () => {
  const [applicationNo, setApplicationNo] = useState<string>('');
  const handleSearch = (): void => {};

  const { data, loading, refetch } = useQuery(
    gql`
      query ($applicationId: String!) {
        offer(applicationId: $applicationId) {
          applicationNo
          applicantName
          status
          entryYear
          isFirmOffer
        }
      }
    `,
    {
      variables: {
        applicationId: applicationNo,
      },
      skip: applicationNo === '' || applicationNo === undefined,
    },
  );

  const WithDrawOffer = (props: {
    applicationId: string;
    refetch: () => void;
  }): React.ReactNode => {
    const [withdrawOffer] = useMutation(gql`
      mutation ($applicationId: String!) {
        rejectOffer(applicationId: $applicationId)
      }
    `);

    const formik = useFormik({
      initialValues: {},
      onSubmit: async () => {
        await withdrawOffer({
          variables: {
            applicationId: props.applicationId,
          },
        });
        props.refetch();
      },
    });

    return (
      <form onSubmit={formik.handleSubmit}>
        <p>
          <i>
            This action is dangerous. Please double confirm the offer
            application number and name.
          </i>
        </p>
        <Button type="submit">Submit</Button>
      </form>
    );
  };

  const VirtualApplicationForHKPFS = (props: {
    applicationId: string;
  }): React.ReactNode => {
    const [virtualApplicationForHKPFS] = useMutation(gql`
      mutation ($applicationId: String!) {
        adminCreateVirtualApplication(
          applicationId: $applicationId
          type: HKPFS
        )
      }
    `);

    const formik = useFormik({
      initialValues: {},
      onSubmit: async () => {
        await virtualApplicationForHKPFS({
          variables: {
            applicationId: props.applicationId,
          },
        });
      },
    });

    return (
      <form onSubmit={formik.handleSubmit}>
        <Button type="submit">Submit</Button>
      </form>
    );
  };

  const offerInformation = [
    {
      label: 'Application No',
      value: data?.offer?.applicationNo,
    },
    {
      label: 'Applicant Name',
      value: data?.offer?.applicantName,
    },
    {
      label: 'Entry Year',
      value: data?.offer?.entryYear,
    },
    {
      label: 'Status',
      value: data?.offer?.status,
    },
    {
      label: 'is Firm Offer',
      value: data?.offer?.isFirmOffer === true ? 'Yes' : 'No',
    },
  ];

  const rows = [
    {
      label: <b>Withdraw Offer</b>,
      value: <WithDrawOffer applicationId={applicationNo} refetch={refetch} />,
    },
    {
      label: <b>HKPFS Virtual Application</b>,
      value: <VirtualApplicationForHKPFS applicationId={applicationNo} />,
    },
  ];

  return loading ? (
    <Loading />
  ) : (
    <div>
      <div>
        <p>
          <i>
            Please copy the Application ID in the monitor page. Enhancement may
            be brought soon.
          </i>
        </p>
        <HandleApplicationSearch
          applicationNo={applicationNo}
          setApplicationNo={setApplicationNo}
          handleSearch={handleSearch}
          placeholder={'Application ID'}
        />
      </div>
      <Card
        sx={{
          boxShadow: 'lg',
          backgroundColor: '#f5f5f5',
          padding: '2rem 4rem',
          width: '100%',
        }}
      >
        <RowDisplay rows={offerInformation} />
      </Card>
      <Card
        sx={{
          boxShadow: 'lg',
          backgroundColor: '#f5f5f5',
          padding: '2rem 4rem',
          width: '100%',
        }}
      >
        <RowDisplay rows={rows} needHr={true} />
      </Card>
    </div>
  );
};
